import { useCallback, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useCreateFolder } from '@frameio/assets/operations';
import { useDeleteCollection } from '@frameio/collections/operations';
import { useApolloClient } from '@frameio/graphql-client';
import { GetShareForShareBuilder } from '@frameio/graphql-client/generated';
import { useCreateAssetMenu } from '@frameio/menu-items/components';
import type { ProjectTreeItem, useProjectTree } from '@frameio/project-tree';
import {
  useCloseNavigationMenu,
  useNavigationMenuState,
  useSetRenameItem,
} from '@frameio/project-tree/contexts';
import {
  useActiveCollectionId,
  useActiveProjectId,
  useActiveShareId,
  useContextMenuIsOpen,
  useContextMenuPosition,
  useContextMenuTarget,
} from '@frameio/shared/contexts';
import { getLink } from '@frameio/shared/routing';
import { Menu } from '@frameio/vapor';
import { CollectionContextMenu } from './CollectionContextMenu';
import { FolderContextMenu } from './FolderContextMenu';
import TEXT from './generated/TEXT/ProjectNavigation_ContextMenu';

type Props = {
  rootFolderId: string;
} & Pick<ReturnType<typeof useProjectTree>, 'toggleItem'>;

export function ContextMenu({ rootFolderId, toggleItem }: Props) {
  const client = useApolloClient();
  const projectId = useActiveProjectId() ?? '';
  const activeCollectionId = useActiveCollectionId();
  const activeShareId = useActiveShareId();
  const router = useRouter();

  const closeNavigationMenu = useCloseNavigationMenu();
  const isShowing = useContextMenuIsOpen();
  const position = useContextMenuPosition();
  const selectedItem = useContextMenuTarget<ProjectTreeItem | null>();
  const prevSelectedItem = useRef(selectedItem);

  const createFolder = useCreateFolder({
    onSuccess(newFolder) {
      setRenameItem({
        id: newFolder.id,
        type: 'folder',
      });
    },
  });
  const { targetItem, targetItemRef } = useNavigationMenuState();
  const setRenameItem = useSetRenameItem();
  const isInlineEditing = targetItem !== null;

  const {
    deleteDialog: deleteCollectionDialog,
    requestDelete: deleteCollection,
  } = useDeleteCollection({
    projectId,
    onSuccess: (data) => {
      const activeShare = client.cache.readQuery({
        query: GetShareForShareBuilder,
        variables: { shareId: activeShareId },
      });

      const deletedCollectionId = data?.deleteCollection?.collection?.id;
      const isCollectionShare =
        activeShare?.share?.__typename === 'CollectionShare' &&
        deletedCollectionId === activeShare?.share?.collection?.id;

      if (deletedCollectionId === activeCollectionId || isCollectionShare) {
        const href = getLink({ page: 'folder', projectId });
        router.push(href);
      }
    },
  });

  const handleMenuVisibilityChange = useCallback(
    (newShowing: boolean) => {
      // Only set focus on the folder when closing it, not renaming it
      if (!newShowing && !isInlineEditing) targetItemRef?.current?.focus();
      closeNavigationMenu();
    },
    [isInlineEditing, closeNavigationMenu, targetItemRef]
  );

  const handleRenameSelect = useCallback(() => {
    if (selectedItem) {
      setRenameItem(selectedItem);
    }
  }, [selectedItem, setRenameItem]);

  const handleNewFolderSelect = useCallback(
    (folderId: string, restricted: boolean) => {
      toggleItem(
        {
          id: folderId,
          type: 'folder',
        },
        true
      );

      createFolder({
        parentId: folderId,
        projectId: projectId ?? '',
        restricted,
      });
    },
    [createFolder, projectId, toggleItem]
  );

  const item = selectedItem ?? prevSelectedItem.current;

  const createAssetMenu = useCreateAssetMenu({
    disableFolderButtons: false,
    enableAddWebReviewButton: false,
    folderId: rootFolderId,
    // override the createFolder call in useCreateAssetMenu so we can target
    // the folder in the ProjectTree for renaming
    onCreateFolder(parentId, restricted) {
      createFolder({
        parentId,
        projectId,
        restricted,
      });
    },
    projectId,
    trackingSource: 'ProjectNavFolderMenu',
  });

  return (
    <>
      <Menu
        anchorPosition={position}
        content={
          <>
            {item?.type === 'root-folder' && createAssetMenu?.buttons}

            {item?.type === 'folder' && (
              <FolderContextMenu
                item={item}
                onNewFolderSelect={handleNewFolderSelect}
                onRenameSelect={handleRenameSelect}
              />
            )}

            {item?.type === 'collection' && (
              <CollectionContextMenu
                item={item}
                onDeleteClick={(collection) => {
                  deleteCollection(collection);
                }}
                onRenameSelect={handleRenameSelect}
              />
            )}
          </>
        }
        isShowing={isShowing}
        onRequestChange={handleMenuVisibilityChange}
      />

      {deleteCollectionDialog}
      {createAssetMenu?.input}
    </>
  );
}

TEXT.DEFINE({
  uploadFile: 'Upload Asset',
});
